import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../libs/shared/ui/src/styles/fontStyles';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {CalendarsGridContainer} from './grid/calendarsGridContainer';

/*
 * Props.
 */

interface PreviewCalendarsProps {
  panelId: CalendarsPanelId;
  canPreviewEvent: boolean;
  onPreviewClick: Handler;
}

/*
 * Style.
 */

const StyledPreviewDiv = styled.div`
  position: relative;
  height: 100%;
`;

const StyledOverlayButton = styled(ChromelessButton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 4px;
  align-items: center;
  justify-content: center;

  opacity: 0;
  background-color: ${(p) => p.theme.alphas.white50};
  transition: opacity 300ms;

  &:hover,
  &:focus {
    opacity: 1;
  }
`;

const StyledOverlayTextDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.palette[PaletteColorsEnum.BLUE].shade40};
`;

/*
 * Component.
 */

export const PreviewCalendars: FC<PreviewCalendarsProps> = (props) => {
  const theme = useUserTheme();
  const {panelId, canPreviewEvent, onPreviewClick} = props;
  return (
    <StyledPreviewDiv>
      <CalendarsGridContainer
        panelId={panelId}
        isVisible
        popoverCalendarEventData={undefined}
        onEventClick={undefined}
        canCreateEventOnSelect={false}
        shouldCalendarDisplayWeekends
        isPreviewMode
      />
      {canPreviewEvent && (
        <StyledOverlayButton onClick={onPreviewClick}>
          <Icon name="calendarFilled" color={theme.palette[PaletteColorsEnum.BLUE].shade40} />
          <StyledOverlayTextDiv>
            <Trans id="calendar-preview-open">Open in Calendar</Trans>
          </StyledOverlayTextDiv>
        </StyledOverlayButton>
      )}
    </StyledPreviewDiv>
  );
};
